/* ════════════ NUMBER GO UP ════════════
   The bitcoin page's atmosphere: a pixel candlestick chart crawling
   across the bottom of the screen. It dips, it crashes, it makes a
   new all-time high anyway. Plus the live block height in the hero. */

import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

function chart(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#candles")!;
  const ctx = c.getContext("2d")!;

  const W = 14; // candle slot width
  const BODY = 8; // candle body width
  const UP = "#f7931a";
  const DOWN = "rgba(247, 147, 26, 0.38)";
  const WICK = "rgba(247, 147, 26, 0.5)";

  type Candle = { open: number; close: number; hi: number; lo: number };
  let candles: Candle[] = [];
  let price = 0;
  let offset = 0; // sub-candle scroll position in px

  // drift up on average, crash occasionally, never die
  const next = (): Candle => {
    const crash = Math.random() < 0.04;
    const move = crash
      ? -(8 + Math.random() * 26)
      : (Math.random() - 0.42) * 14;
    const open = price;
    const close = Math.max(6, open + move);
    price = close;
    const hi = Math.max(open, close) + Math.random() * 5;
    const lo = Math.max(2, Math.min(open, close) - Math.random() * 5);
    return { open, close, hi, lo };
  };

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    price = 30 + Math.random() * 20;
    candles = [];
    for (let i = 0; i < Math.ceil(c.width / W) + 2; i++) candles.push(next());
  };
  seed();
  addEventListener("resize", seed, { signal });

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    const base = c.height - 24;
    // the chart climbs forever; rescale so the tail stays on screen
    const peak = Math.max(...candles.map((k) => k.hi));
    const scale = Math.min(2.2, (c.height * 0.38) / peak);
    for (let i = 0; i < candles.length; i++) {
      const k = candles[i]!;
      const x = (i * W - offset) | 0;
      if (x < -W || x > c.width) continue;
      const yo = base - k.open * scale;
      const yc = base - k.close * scale;
      const up = k.close >= k.open;
      ctx.fillStyle = WICK;
      ctx.fillRect(x + (BODY >> 1), (base - k.hi * scale) | 0, 2, ((k.hi - k.lo) * scale) | 0 || 1);
      ctx.fillStyle = up ? UP : DOWN;
      const top = Math.min(yo, yc) | 0;
      const h = Math.max(2, Math.abs(yo - yc) | 0);
      ctx.fillRect(x, top, BODY, h);
    }
  };

  if (REDUCED) {
    draw();
    return;
  }
  const tick = () => {
    if (signal.aborted) return;
    offset += 0.25;
    if (offset >= W) {
      offset -= W;
      candles.shift();
      candles.push(next());
    }
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

/* live block height for the hero stat strip */
async function blockHeight() {
  const el = document.querySelector("#height")!;
  try {
    const res = await fetch("https://mempool.space/api/blocks/tip/height");
    if (!res.ok) throw new Error(String(res.status));
    const n = Number(await res.text());
    if (!Number.isFinite(n)) throw new Error("not a number");
    el.textContent = n.toLocaleString("en-US");
  } catch {
    el.textContent = "900k+"; // close enough; run a node and verify
  }
}

onPage((signal) => {
  chart(signal);
  blockHeight();
});
